import { MinusIcon, PlusIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import ArrowExpandIcon from '../../../assets/icons/arrow-expand.svg?react';
import FitToScreenIcon from '../../../assets/icons/fit-to-screen.svg?react';
import flipIcon from '../../../assets/icons/flip.svg?react';
import { useScreenTools } from '../../../hooks/potree/useScreenTools';
import { Project } from '../../../types/graphqlTypes';
import classNames from 'classnames';
import { memo, useContext, useMemo, useState, useEffect } from 'react';
import { IconBox } from '../../../components/IconBox';
import { Tooltip } from '../../../components/Tooltip';
import { T } from '../../../translation/src';
import { RendererContext } from '../../../contexts/RendererContext';
import { ShortcutsModal } from '../../../components/Modal/ShortcutsModal';
import useOpen from '../../../hooks/useOpen';

interface ScreenToolsProps {
  project?: Pick<Project, 'id' | 'mapVisible' | 'settings'>;
}

const ScreenTools_: React.FC2<ScreenToolsProps> = ({ className, project, ...props }) => {
  const [{ fitToScreen, onFullScreen, flip2DView }, { onZoomInStart, onZoomOutStart, onZoomEnd }] = useScreenTools({
    project,
  });
  const renderer = useContext(RendererContext);
  const viewer = renderer.viewer;
  const [canFlip, setCanFlip] = useState(false);
  const [fullScreenEnabled, setFullScreenEnabled] = useState(false);

  useEffect(() => {
    if (!viewer?.personaManager) return;

    const viewManager = viewer.personaManager.viewManager;
    if (!viewManager) return;

    const updateCanFlip = () => {
      const activePersona = viewer.personaManager.getActivePersona();
      setCanFlip(activePersona?.canFlip() || false);
    };

    const updateFullScreen = () => {
      const currentPersona = viewer.personaManager.getActivePersona();
      setFullScreenEnabled(document.fullscreenEnabled && currentPersona?.name === 'monitoring');
    };

    updateCanFlip();
    updateFullScreen();

    viewManager.addEventListener('view_changed', updateCanFlip);
    viewer.personaManager.addEventListener('persona_manager_stack_changed', updateFullScreen);

    return () => {
      viewManager.removeEventListener('view_changed', updateCanFlip);
      viewer.personaManager.removeEventListener('persona_manager_stack_changed', updateFullScreen);
    };
  }, [viewer]);

  return (
    <div className={classNames('flex gap-px z-10 w-auto', className)} {...props}>
      {canFlip && (
        <Tooltip message={<T _str="flip the 2D view" swc />} delay={0}>
          <IconBox icon={flipIcon} onClick={flip2DView} className="rounded-l-lg" />
        </Tooltip>
      )}
      {fullScreenEnabled && (
        <Tooltip message={<T _str="full screen" swc />} delay={0}>
          <IconBox icon={ArrowExpandIcon} onClick={onFullScreen} className={!canFlip ? 'rounded-l-lg' : ''} />
        </Tooltip>
      )}
      <Tooltip message={<T _str="fit to screen" swc />} delay={0}>
        <IconBox icon={FitToScreenIcon} onClick={fitToScreen} className={!(canFlip || fullScreenEnabled) ? 'rounded-l-lg' : ''} />
      </Tooltip>
      <Tooltip message={<T _str="zoom out" swc />} delay={0}>
        <IconBox icon={MinusIcon} onMouseDown={onZoomOutStart} onMouseUp={onZoomEnd} />
      </Tooltip>
      <Tooltip message={<T _str="zoom in" swc />} delay={0}>
        <IconBox icon={PlusIcon} onMouseDown={onZoomInStart} onMouseUp={onZoomEnd} className="rounded-r-lg" />
      </Tooltip>
    </div>
  );
};

const Shortcuts_: React.FC2<ScreenToolsProps> = ({ className, project, ...props }) => {
  const { open, onClose, onOpen } = useOpen();

  const polylinePolygonShortcuts = useMemo(() => {
    return [
      { description: <T _str="add new point" swc />, shortcuts: [<T _str="click Left" swc />] },
      { description: <T _str="remove last point" swc />, shortcuts: [<T _str="click Right" swc />] },
      { description: <T _str="snap angles" swc />, shortcuts: ['⇧'] },
      {
        description: <T _str="complete shape" swc />,
        shortcuts: [<T _str="double click" swc />, <T _str="⏎" swc />],
      },
    ];
  }, []);

  const shortcuts = useMemo(
    () => [
      {
        group: 'navigation' as const,
        shortcuts: [
          { description: <T _str="zoom to cursor position" swc />, shortcuts: [<T _str="double click" swc />] },
          {
            description: <T _str="orbit camera" swc />,
            shortcuts: [<T _str="click Left" swc />, <T _str="drag" swc />],
          },
          { description: <T _str="pan view" swc />, shortcuts: [<T _str="click Right" swc />, <T _str="drag" swc />] },
          { description: <T _str="zoom in/out" swc />, shortcuts: [<T _str="mouse wheel" swc />] },
        ],
      },
      {
        group: 'line' as const,
        shortcuts: polylinePolygonShortcuts,
      },
      {
        group: 'polygon' as const,
        shortcuts: polylinePolygonShortcuts,
      },
      {
        group: 'surface' as const,
        shortcuts: [
          { description: <T _str="snap to horizontal/vertical" swc />, shortcuts: ['⇧'] },
          { description: <T _str="refine best-fit plane" swc />, shortcuts: [<T _str="click Left" swc />] },
          { description: <T _str="undo last cursor position" swc />, shortcuts: [<T _str="click Right" swc />] },
          {
            description: <T _str="create surface" swc />,
            shortcuts: [<T _str="double click" swc />, <T _str="⏎" swc />],
          },
        ],
      },
      {
        group: 'floorplan' as const,
        shortcuts: [
          { description: <T _str="create perpendicular angles" swc />, shortcuts: ['⇧'] },
          { description: <T _str="refine best-fit plane" swc />, shortcuts: ['Ctrl'] },
          { description: <T _str="change base plane" swc />, shortcuts: [<T _str="Alt" swc />] },
          { description: <T _str="add new segment" swc />, shortcuts: [<T _str="click Left" swc />] },
          { description: <T _str="remove last segment" swc />, shortcuts: [<T _str="click Right" swc />] },
          { description: <T _str="create object" swc />, shortcuts: [<T _str="double click" swc />] },
        ],
      },
      {
        group: 'magic wand' as const,
        shortcuts: [
          { description: <T _str="add detected object" swc />, shortcuts: [<T _str="click Left" swc />] },
          { description: <T _str="remove object" swc />, shortcuts: [<T _str="click Right" swc />] },
          {
            description: <T _str="create cluster" swc />,
            shortcuts: [<T _str="double click" swc />, <T _str="⏎" swc />],
          },
          { description: <T _str="add all in lasso" swc />, shortcuts: ['Ctrl'] },
        ],
      },
      {
        group: 'box' as const,
        shortcuts: [
          {
            description: <T _str="create box" swc />,
            shortcuts: [<T _str="click Left" swc />],
          },
        ],
      },
      {
        group: 'screenshot' as const,
        shortcuts: [
          { description: <T _str="take screenshot of entire window" swc />, shortcuts: [<T _str="click Left" swc />] },
          {
            description: <T _str="select area for screenshot" swc />,
            shortcuts: [<T _str="click Left" swc />, <T _str="drag" swc />],
          },
        ],
      },
    ],
    [polylinePolygonShortcuts],
  );

  return (
    <>
      <div className={classNames('flex gap-px z-10 w-auto', className)} {...props}>
        <Tooltip message={<T _str="shortcuts" swc />} delay={0}>
          <IconBox icon={LightBulbIcon} onClick={onOpen} className="rounded-lg" />
        </Tooltip>
      </div>
      <ShortcutsModal open={open} onClose={onClose} shortcuts={shortcuts} />
    </>
  );
};

export const ScreenTools = memo(ScreenTools_);
export const Shortcuts = memo(Shortcuts_);
